/**
 * O arrasto do card do funil, com gravidade (26/10/2026).
 *
 * ── Por que à mão, e não o arrasto nativo nem uma biblioteca ──────────────
 *
 * O arrasto nativo (`draggable`) desenha uma FOTO fixa do card, que o navegador
 * move: não dá para girá-la, balançá-la nem animar o pouso. As bibliotecas de
 * arrastar resolvem ordenação e acessibilidade, que este quadro já tem (o
 * "Mover" é a alternativa por teclado), e a física continuaria sendo nossa. O
 * que sobra é pouco: ponteiro, uma mola e uma animação de pouso — sem
 * dependência nova de interface (CLAUDE.md do `apps/web`).
 *
 * ── A física ───────────────────────────────────────────────────────────────
 *
 * O card fica PENDURADO no ponto em que foi pego, como um quadro num prego:
 *
 * - em repouso, ele cai para o lado mais pesado (pego pela esquerda, a direita
 *   desce) — o ângulo de repouso sai da distância do ponto ao centro;
 * - em movimento, ele fica para trás e balança: o alvo do ângulo é o de repouso
 *   mais a velocidade horizontal do ponteiro, e uma mola amortecida
 *   (frequência ~2 Hz, amortecimento abaixo do crítico) leva o ângulo até lá —
 *   por isso ele passa do ponto e volta, como um pêndulo;
 * - ao soltar numa coluna, ele desliza até a vaga e se endireita, e o card de
 *   verdade aparece no lugar com um leve assentar.
 *
 * Com "reduzir movimento" ligado no sistema, sem balanço nem inclinação: o card
 * só acompanha o ponteiro e pousa.
 *
 * ── O toque ────────────────────────────────────────────────────────────────
 *
 * No toque, o arrasto começa só depois de segurar um instante: deslizar o dedo
 * é rolar o quadro, e um arrasto a cada rolagem tornaria o funil inutilizável
 * no celular.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

/** O que o quadro diz ao arrasto. */
export interface ComportamentoDoArrasto<T> {
  /** A etapa em que o item está agora (com o otimismo do movimento em curso). */
  readonly etapaDe: (item: T) => string;
  /** A coluna aceita o item? */
  readonly podeSoltar: (item: T, etapa: string) => boolean;
  /** Soltou numa coluna que aceita: é o pedido de mover. */
  readonly aoSoltar: (item: T, etapa: string) => void;
  /** A chave do item no DOM (`data-card-id`): é por ela que o pouso acha a vaga. */
  readonly chaveDe: (item: T) => string;
  /** Começou e terminou, para quem mais precisa saber (o quadro). */
  readonly aoMudar?: (item: T | null) => void;
}

export interface EstadoDoArrasto<T> {
  /** O item no ar, ou `null`. */
  readonly noAr: T | null;
  /** A etapa sob o ponteiro que aceita o item. */
  readonly alvo: string | null;
  /** A altura do card no ar: a vaga na coluna tem o tamanho dele. */
  readonly altura: number;
  /** A chave do item que está pousando: o card de verdade espera, invisível. */
  readonly pousando: string | null;
  /** O `onPointerDown` do card. */
  readonly segurar: (evento: PointerEvent<HTMLElement>, item: T) => void;
}

/** Quanto o ponteiro anda antes de virar arrasto: menos que isso é clique. */
const LIMIAR_DO_MOUSE = 5;
/** Quanto o dedo pode tremer enquanto segura. */
const LIMIAR_DO_TOQUE = 8;
/** Quanto tempo segurar o dedo para começar. */
const ESPERA_DO_TOQUE_MS = 220;
/** A mola do pêndulo (por segundo²) e o amortecimento (por segundo). */
const RIGIDEZ = 160;
const AMORTECIMENTO = 11;
/** Graus de inclinação por px/s de velocidade horizontal, e o teto. */
const INCLINACAO_POR_VELOCIDADE = 0.018;
const INCLINACAO_MAXIMA = 22;
/** A inclinação de repouso, pela distância do ponto pego ao centro. */
const PESO_DO_LADO = 10;
/** O card sobe um pouco ao ser pego. */
const ESCALA_NO_AR = 1.04;
/** O pouso: o tempo e a curva (um pouco de passagem no fim, como um encaixe). */
const POUSO_MS = 380;
const CURVA_DO_POUSO = 'cubic-bezier(0.2, 0.9, 0.25, 1.12)';
/** A borda do quadro que faz ele rolar sozinho durante o arrasto. */
const BORDA_DE_ROLAGEM = 72;

const CONTROLE = 'button, select, a, input, textarea, label, [role="menu"], [role="menuitem"]';

function movimentoReduzido(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

const limitar = (valor: number, maximo: number) => Math.max(-maximo, Math.min(maximo, valor));

export function useArrastoComGravidade<T>(
  comportamento: ComportamentoDoArrasto<T>,
): EstadoDoArrasto<T> {
  const [noAr, definirNoAr] = useState<T | null>(null);
  const [alvo, definirAlvo] = useState<string | null>(null);
  const [altura, definirAltura] = useState(0);
  const [pousando, definirPousando] = useState<string | null>(null);

  // O comportamento muda a cada render; o arrasto em curso lê o de agora.
  const atual = useRef(comportamento);
  atual.current = comportamento;
  const alvoAtual = useRef<string | null>(null);
  const encerrar = useRef<(() => void) | null>(null);

  // Saiu da tela no meio do arrasto: nada fica pendurado no `body`.
  useEffect(() => () => encerrar.current?.(), []);

  const segurar = useCallback((evento: PointerEvent<HTMLElement>, item: T) => {
    if (evento.button !== 0 || encerrar.current) return;
    if ((evento.target as Element).closest(CONTROLE)) return;

    const elemento = evento.currentTarget;
    const toque = evento.pointerType === 'touch';
    const inicio = { x: evento.clientX, y: evento.clientY };
    const reduzido = movimentoReduzido();

    let comecou = false;
    let espera: ReturnType<typeof setTimeout> | null = null;
    let quadro = 0;
    let casca: HTMLDivElement | null = null;
    let rolagem: HTMLElement | null = null;
    // A física: onde está o ponteiro, o ângulo e a velocidade dele.
    const fisica = {
      x: inicio.x,
      y: inicio.y,
      pegaX: 0,
      pegaY: 0,
      anterior: inicio.x,
      velocidade: 0,
      angulo: 0,
      giro: 0,
      repouso: 0,
      escala: 1,
      tempo: 0,
    };

    const desenhar = () => {
      if (!casca) return;
      casca.style.transform =
        `translate3d(${fisica.x - fisica.pegaX}px, ${fisica.y - fisica.pegaY}px, 0) ` +
        `rotate(${fisica.angulo}deg) scale(${fisica.escala})`;
    };

    const passo = (agora: number) => {
      const dt = fisica.tempo ? Math.min(0.032, (agora - fisica.tempo) / 1000) : 0.016;
      fisica.tempo = agora;
      // A velocidade suavizada: o ponteiro chega aos saltos, e a média curta
      // tira o tremido sem atrasar o balanço.
      const instantanea = (fisica.x - fisica.anterior) / Math.max(dt, 0.001);
      fisica.anterior = fisica.x;
      fisica.velocidade += (instantanea - fisica.velocidade) * 0.25;

      if (!reduzido) {
        const alvoDoAngulo = limitar(
          fisica.repouso + fisica.velocidade * INCLINACAO_POR_VELOCIDADE,
          INCLINACAO_MAXIMA,
        );
        const aceleracao = RIGIDEZ * (alvoDoAngulo - fisica.angulo) - AMORTECIMENTO * fisica.giro;
        fisica.giro += aceleracao * dt;
        fisica.angulo += fisica.giro * dt;
      }
      fisica.escala += (ESCALA_NO_AR - fisica.escala) * 0.2;

      // Perto da borda do quadro, ele rola sozinho: dá para levar o card a uma
      // coluna que estava fora da vista.
      if (rolagem) {
        const caixa = rolagem.getBoundingClientRect();
        if (fisica.x < caixa.left + BORDA_DE_ROLAGEM) rolagem.scrollLeft -= 14;
        else if (fisica.x > caixa.right - BORDA_DE_ROLAGEM) rolagem.scrollLeft += 14;
      }

      desenhar();
      quadro = requestAnimationFrame(passo);
    };

    /** A coluna sob o ponteiro, se ela aceita o item. */
    const colunaSobOPonteiro = (): string | null => {
      const sob = document.elementFromPoint(fisica.x, fisica.y);
      const coluna = sob?.closest<HTMLElement>('[data-etapa-alvo]');
      const etapa = coluna?.dataset['etapaAlvo'] ?? null;
      if (!etapa || etapa === atual.current.etapaDe(item)) return null;
      return atual.current.podeSoltar(item, etapa) ? etapa : null;
    };

    const comecar = () => {
      comecou = true;
      const caixa = elemento.getBoundingClientRect();
      fisica.pegaX = inicio.x - caixa.left;
      fisica.pegaY = inicio.y - caixa.top;
      // Pego pela esquerda, a direita desce: o lado mais longe do ponto pesa.
      fisica.repouso = reduzido
        ? 0
        : limitar(
            ((caixa.width / 2 - fisica.pegaX) / caixa.width) * PESO_DO_LADO * 2,
            PESO_DO_LADO,
          );
      rolagem = elemento.closest<HTMLElement>('.quadro');

      casca = document.createElement('div');
      casca.className = 'card-no-ar';
      casca.style.width = `${caixa.width}px`;
      casca.style.transformOrigin = `${fisica.pegaX}px ${fisica.pegaY}px`;
      const copia = elemento.cloneNode(true) as HTMLElement;
      copia.removeAttribute('data-card-id');
      copia.setAttribute('aria-hidden', 'true');
      casca.appendChild(copia);
      document.body.appendChild(casca);
      document.body.classList.add('arrastando-card');

      definirAltura(caixa.height);
      definirNoAr(item);
      atual.current.aoMudar?.(item);
      desenhar();
      quadro = requestAnimationFrame(passo);
    };

    const mover = (e: globalThis.PointerEvent) => {
      if (e.pointerId !== evento.pointerId) return;
      fisica.x = e.clientX;
      fisica.y = e.clientY;
      const andou = Math.hypot(e.clientX - inicio.x, e.clientY - inicio.y);
      if (!comecou) {
        if (toque) {
          // O dedo andou antes do tempo: é rolagem, e o arrasto desiste.
          if (andou > LIMIAR_DO_TOQUE) desistir();
          return;
        }
        if (andou < LIMIAR_DO_MOUSE) return;
        comecar();
      }
      e.preventDefault();
      const etapa = colunaSobOPonteiro();
      if (etapa !== alvoAtual.current) {
        alvoAtual.current = etapa;
        definirAlvo(etapa);
      }
    };

    const tirarOuvintes = () => {
      if (espera) clearTimeout(espera);
      globalThis.removeEventListener('pointermove', mover);
      globalThis.removeEventListener('pointerup', soltar);
      globalThis.removeEventListener('pointercancel', cancelar);
      globalThis.removeEventListener('touchmove', segurarARolagem);
    };

    const limpar = () => {
      cancelAnimationFrame(quadro);
      casca?.remove();
      casca = null;
      document.body.classList.remove('arrastando-card');
      alvoAtual.current = null;
      definirAlvo(null);
      definirNoAr(null);
      definirPousando(null);
      atual.current.aoMudar?.(null);
      encerrar.current = null;
    };

    function desistir() {
      tirarOuvintes();
      if (comecou) limpar();
      else encerrar.current = null;
    }

    function soltar(e: globalThis.PointerEvent) {
      if (e.pointerId !== evento.pointerId) return;
      tirarOuvintes();
      if (!comecou) {
        encerrar.current = null;
        return;
      }
      /*
       * O clique que vem junto da soltura não abre a ficha do card. Ele chega
       * logo depois do `pointerup` — ou nunca (no toque, ou soltando fora da
       * janela). Por isso o filtro sai sozinho num instante: preso, ele
       * engoliria o próximo clique da pessoa em qualquer lugar.
       */
      const engolir = (c: MouseEvent) => c.stopPropagation();
      globalThis.addEventListener('click', engolir, { capture: true, once: true });
      setTimeout(() => globalThis.removeEventListener('click', engolir, { capture: true }), 250);
      cancelAnimationFrame(quadro);

      const etapa = alvoAtual.current;
      const chave = atual.current.chaveDe(item);
      if (etapa) atual.current.aoSoltar(item, etapa);
      definirPousando(chave);
      alvoAtual.current = null;
      definirAlvo(null);

      // Dois quadros: o React desenha o card na coluna nova (ou o devolve à de
      // origem, quando o movimento pediu uma pergunta antes), e só então a vaga
      // tem lugar para ser medida.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const vaga = document.querySelector<HTMLElement>(`[data-card-id="${CSS.escape(chave)}"]`);
          if (!casca || !vaga) {
            limpar();
            return;
          }
          const destino = vaga.getBoundingClientRect();
          const pouso = casca.animate(
            [
              { transform: casca.style.transform },
              {
                transform: `translate3d(${destino.left}px, ${destino.top}px, 0) rotate(0deg) scale(1)`,
              },
            ],
            { duration: reduzido ? 120 : POUSO_MS, easing: CURVA_DO_POUSO, fill: 'forwards' },
          );
          // A origem da rotação muda para o canto no pouso: o card se assenta
          // pela vaga, e não gira em volta do ponto em que foi pego.
          casca.style.transformOrigin = '0 0';
          pouso.onfinish = () => {
            vaga.dataset['assentou'] = 'sim';
            setTimeout(() => delete vaga.dataset['assentou'], 360);
            limpar();
          };
        }),
      );
    }

    /*
     * Cancelado não é soltar: o sistema interrompeu o gesto (uma ligação, um
     * gesto do aparelho), e a pessoa não largou o card em lugar nenhum. Ele
     * volta para onde estava, sem mover a oportunidade.
     */
    function cancelar(e: globalThis.PointerEvent) {
      if (e.pointerId !== evento.pointerId) return;
      desistir();
    }

    // No toque, a página não pode rolar enquanto o card está no ar.
    function segurarARolagem(e: TouchEvent) {
      if (comecou && e.cancelable) e.preventDefault();
    }

    globalThis.addEventListener('pointermove', mover, { passive: false });
    globalThis.addEventListener('pointerup', soltar);
    globalThis.addEventListener('pointercancel', cancelar);
    globalThis.addEventListener('touchmove', segurarARolagem, { passive: false });
    if (toque) espera = setTimeout(comecar, ESPERA_DO_TOQUE_MS);
    encerrar.current = desistir;
  }, []);

  return { noAr, alvo, altura, pousando, segurar };
}
